/**
 * `src/lib/ui/reader/position.ts` — "continue reading", through the FROZEN
 * `positions` table.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  THE POSITION IS AN ANCHOR, NOT A SCROLL OFFSET
 * ═══════════════════════════════════════════════════════════════════════════
 * `schema.ts`'s `Position` row already decided this and the reader only has to
 * obey it: the row carries an `Anchor` and a `start_pos`, and `scroll_ratio` is
 * a decoration on top. So what is saved is "you were on 2:255", expressed as
 * `hafs:2:255` — the same coordinate a note is anchored with, page-independent
 * by construction (SPEC §3.1: "`AnchorKind` deliberately has no 'page' member;
 * pages are a rendering, not a place").
 *
 * That is what makes restore survive a reading-size change. A stored pixel
 * offset means a different ayah at a different type scale; a stored `Pos` means
 * the same ayah at every type scale, on every device, forever.
 *
 * `scroll_ratio` is therefore defined HERE, because the schema does not define
 * it and an undefined number in a frozen row is how two readers of the same
 * field end up disagreeing:
 *
 *     scroll_ratio = how far INTO THE ANCHORED AYAH's block the viewport top
 *                    was, 0..1. NOT a ratio of the document.
 *
 * A document ratio would be the obvious reading and it is the wrong one: block
 * heights do not scale uniformly with the type scale (a one-line ayah gains one
 * line, a twenty-one-line ayah gains ten), so a document ratio moves even when
 * the reader has not. Within one block the assumption "the text reflows roughly
 * proportionally" is true enough for a scroll offset, and the error is bounded
 * by one ayah's height rather than by the length of al-Baqarah.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  AND IT IS DELIBERATELY NOT SYNCED
 * ═══════════════════════════════════════════════════════════════════════════
 * `positions` is in `DEVICE_LOCAL_TABLES`. The schema says why, and the reader
 * repeats it because it is a product decision and not a plumbing one: "where I
 * am on my phone" and "where I am on my laptop" are different facts. The id is
 * `${surface}:${device_id}`, so each device keeps exactly one row per surface
 * and no merge is ever required.
 */

import {
	ayahIdOf,
	ayahIdOfPos,
	ayahRef,
	locationOf,
	parseRef,
	posOf,
	tryParseRef,
	type AyahId
} from '$lib/anchor';
import { positionId } from '$lib/db/ids';
import { db, type Position } from '$lib/db/schema';

import { deviceIdentity } from './device';

/** The surface this module writes. `'azkar'` is M7's. */
const SURFACE = 'quran' as const;

/** A restored reading position, already validated. */
export interface ReadingPosition {
	surah: number;
	ayah: number;
	ayahId: AyahId;
	/** the durable coordinate — `'hafs:2:255'`. */
	ref: string;
	/** how far into that ayah's block the viewport top was, 0..1. */
	fraction: number;
	updatedAt: number;
	/** `meta.device_label`, `''` until M4 lets the user name the device. */
	deviceLabel: string;
}

function clamp01(value: unknown): number {
	if (typeof value !== 'number' || !Number.isFinite(value)) return 0;
	return value < 0 ? 0 : value > 1 ? 1 : value;
}

/**
 * Remember where the reader is.
 *
 * Returns `false` when the write did not land, so a caller can decline to
 * promise something it did not do. It never throws: failing to record a scroll
 * position must not interrupt reading.
 */
export async function saveReadingPosition(ayahId: AyahId, fraction: number): Promise<boolean> {
	let place: { surah: number; ayah: number };
	try {
		place = locationOf(ayahId);
	} catch {
		return false;
	}
	try {
		const device = await deviceIdentity();
		if (!device.persisted) return false;
		const ref = ayahRef(place.surah, place.ayah);
		const row: Position = {
			id: positionId(SURFACE, device.id),
			surface: SURFACE,
			device_id: device.id,
			device_label: device.label,
			/* `parseRef` and not a hand-built object: the anchor stored here has
			 * to be the same shape `rebuildAnchor` would produce from the same
			 * `ref`, or a later consumer comparing them finds a difference that
			 * is not there. */
			anchor: parseRef(ref),
			start_pos: posOf(ayahId, 0),
			scroll_ratio: clamp01(fraction),
			updated_at: Date.now()
		};
		await db.positions.put(row);
		return true;
	} catch {
		return false;
	}
}

/**
 * Where this device left off, or `null`.
 *
 * `ref` is the authority and `start_pos` is checked against it. They can only
 * disagree if something wrote the row without going through this module — and
 * in that case the ref wins, because it is the field the whole architecture
 * treats as truth. A row whose ref cannot be parsed at all is simply not a
 * position this build can restore; it is left in place rather than deleted, so
 * that M4's Repair screen still has it.
 */
export async function loadReadingPosition(): Promise<ReadingPosition | null> {
	try {
		const device = await deviceIdentity();
		const row = await db.positions.get(positionId(SURFACE, device.id));
		if (!row) return null;
		const anchor = tryParseRef(row.anchor?.ref ?? '');
		if (!anchor || anchor.kind !== 'ayah') return null;
		if (anchor.surah === undefined || anchor.ayah === undefined) return null;
		const ayahId = ayahIdOf(anchor.surah, anchor.ayah);
		return {
			surah: anchor.surah,
			ayah: anchor.ayah,
			ayahId,
			ref: anchor.ref,
			fraction: clamp01(row.scroll_ratio),
			updatedAt: typeof row.updated_at === 'number' ? row.updated_at : 0,
			deviceLabel: typeof row.device_label === 'string' ? row.device_label : ''
		};
	} catch {
		return null;
	}
}

/**
 * The ayah a stored `start_pos` names, for a caller that has the row already.
 * Exported because `/about/diagnostics` and the Repair screen will both want to
 * read a `Pos` without re-deriving `ayahId * 1024 + word` by hand.
 */
export function ayahOfStoredPos(startPos: number): AyahId | null {
	try {
		return ayahIdOfPos(startPos);
	} catch {
		return null;
	}
}
